import React, { useState, useCallback, useMemo, useContext } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import {DELETE_RECEIVED_FAX, MANAGED_RECEIVED_FAXES_SUBSCRIPTION, RECEIVED_FAXES_SUBSCRIPTION} from '../gql/FaxQueries';
import {useMutation} from '../../../common/apollo-graphql';
import { useSubscription } from '@apollo/client';
import ReceivedFaxList from './ReceivedFaxList';
import {SelectedFaxProvider} from './SelectedFaxContext';
import ReceivedFaxDetails from './ReceivedFaxDetails';
import Swal from 'sweetalert2';
import {MyTypography} from '../../common/components';
import {FROM_FAX_NUMBER} from '../constants/fax.consants';
import Error from '../../common/Error';
import {closeLoadingPopup, showLoadingPopup} from '../../common/components/MyLoadingDialog';
import AuthContext from '../../auth/context/AuthContext';

function MyFaxNumber() {
  return (
    <MyTypography variant="subtitle1"
                  sx={{color: 'primary.50'}}>
      Fax: {FROM_FAX_NUMBER}
    </MyTypography>
  );
}

export function useManageReceivedFaxes(clinicId=null) {
    const [selectedFax, setSelectedFax] = useState(null);

    const gqlOptions = useMemo(() => {
        if (clinicId) {
            return {
                subscription: RECEIVED_FAXES_SUBSCRIPTION,
                params: {
                    variables: {
                        clinicId,
                    },
                }
            };
        }

        return {
            subscription: MANAGED_RECEIVED_FAXES_SUBSCRIPTION,
            params: {}
        };
    }, [clinicId]);

    const { data, loading, error } = useSubscription(gqlOptions.subscription, {
        ...gqlOptions.params,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    const [deleteReceivedFax] = useMutation(DELETE_RECEIVED_FAX, {
        fetchPolicy: 'no-cache',
    });

    const handleDeleteFax = useCallback((faxId) => async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will permanently delete the fax.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        });

        if (result.isConfirmed) {
            try {
                showLoadingPopup('Please wait. Deleting fax.');
                await deleteReceivedFax({ variables: { faxId } });
                closeLoadingPopup();
                setSelectedFax(null);
                Swal.fire('Deleted!', 'The fax has been deleted.', 'success');
            } catch (err) {
                console.error('Error deleting fax:', err);
                closeLoadingPopup();
                Swal.fire('Error', err.message || 'Failed to delete fax.', 'error');
            }
        }
    }, [deleteReceivedFax]);

    const handleOnClickFaxItem = useCallback((fax) => (event=null) => {
        if (event) event.stopPropagation();
        setSelectedFax(fax);
    }, []);

    const handleCloseFaxDetails = useCallback(() => {
        setSelectedFax(null);
    }, []);

    return {
        data,
        loading,
        error,
        onDeleteFax: handleDeleteFax,
        selectedFax,
        onSelectFax: handleOnClickFaxItem,
        onCloseFaxDetails: handleCloseFaxDetails,
    };
}

export default function ReceivedFaxPage() {
    const currentUser = useContext(AuthContext);
    const clinicId = currentUser?.clinic?.id;
    const { data, loading, error, onDeleteFax, selectedFax, onSelectFax, onCloseFaxDetails } = useManageReceivedFaxes(clinicId);

    return (
        <SelectedFaxProvider value={selectedFax}>
          <Grid container
                sx={{
                  height: '100%',
                  maxHeight: 'calc(100vh - 130px)',
                  display: 'flex',
                }}>
            {/* Sidebar */}
            <Grid item
                  xs={12}
                  sm={6}
                  md={selectedFax ? 3 : 4}
                  sx={{
                    height: 'inherit',
                    overflowY: 'auto',
                    transition: 'width 0.3s ease',
                  }}>
            <Box
                sx={{
                  height: '100%',
                  borderRight: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
            >
              {/* Sidebar Header */}
              <Stack direction="row"
                     alignItems="center"
                     sx={{ borderBottom: 1, borderColor: "divider", p: 2 }}>
                {
                  selectedFax
                  ? <Stack direction="row"
                           justifyContent="flex-end"
                           alignItems="flex-start"
                           width="100%">
                        {/*<Stack direction="row"*/}
                        {/*       alignItems="center"*/}
                        {/*       spacing={1}>*/}
                        {/*  <MyTypography variant="h6">Faxes</MyTypography>*/}
                        {/*  <IconButton size="small" onClick={handleCloseFaxDetails} sx={{ color: 'text.secondary' }}>*/}
                        {/*    <ChevronRight />*/}
                        {/*  </IconButton>*/}
                        {/*</Stack>*/}
                        <MyFaxNumber />
                    </Stack>
                  : <Stack direction="row"
                           justifyContent="space-between"
                           alignItems="flex-start"
                           width="100%">
                      <Box sx={{ opacity: selectedFax ? 0 : 1, transition: "opacity 0.2s" }}>
                        <MyTypography variant="h5" fontWeight="bold">Received Faxes</MyTypography>
                        <MyTypography variant="body2" color="text.secondary">Click any fax to view details</MyTypography>
                      </Box>
                      <MyFaxNumber />
                    </Stack>
                }
              </Stack>
              {/* Sidebar List */}
              {
                error
                ? <Error
                    title="Encountered error while fetching inbound faxes"
                    error={
                      error.hasOwnProperty('graphQLErrors')
                          ? error.graphQLErrors[0]
                          : error
                    }
                  />
                : <ReceivedFaxList
                      data={data}
                      loading={loading}
                      onDeleteFax={onDeleteFax}
                      onClickFaxItem={onSelectFax}
                  />
              }
            </Box>
          </Grid>
            {/* Main Content */}
            <Grid item
                  xs={12}
                  sm={6}
                  md={selectedFax ? 9 : 8}
                  sx={{
                    transition: 'width 0.3s ease',
                  }}>
              <ReceivedFaxDetails onCloseFaxDetails={onCloseFaxDetails} />
            </Grid>
          </Grid>
        </SelectedFaxProvider>
    );
};
